import { Flex, Text } from 'theme-ui'
import { tabs } from './industriesData'
import { Button, SimpleActionMenuItemProps, Svg } from 'ui'
import ArrowRightIcon from 'images/organization/homepage/sections/industries/arrow-right.svg'
import CheckIcon from 'images/organization/homepage/sections/industries/check.svg'
import { DOCS_CONTROL_D_DOMAIN } from 'gatsby-env-variables'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Setter } from 'utils'
import DropdownCheckIcon from 'images/analytics/check.svg'
import SimpleDropdown from 'ui/Dropdown/SimpleDropdown'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { navigate } from 'gatsby'

let intervalId

export default function IndustriesContent(): ReactElement {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const isMobileOrTablet = useBreakpointIndex() < 2

  const setTabInterval = () => {
    intervalId = setInterval(() => {
      setSelectedTabIndex(prevState => {
        if (prevState === tabs.length - 1) {
          return 0
        }
        return prevState + 1
      })
    }, 15000)
  }

  useEffect(() => {
    if (!isMobileOrTablet) {
      setTabInterval()
    }

    return () => {
      clearInterval(intervalId)
    }
  }, [isMobileOrTablet])

  return (
    <Flex
      sx={{
        contentVisibility: 'auto',
        flexDirection: ['column', 'column', 'row'],
        mt: ['4.8rem', '4.8rem', '8rem'],
        gap: ['4.8rem', '4.8rem', '14.8rem'],
        '@media (min-width: 1120px) and (max-width: 1230px)': {
          gap: '7rem',
        },
      }}
    >
      <IndustriesDropdown
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
      />
      <Flex
        sx={{
          display: ['none', 'none', 'flex'],
          width: '49.6rem',
          flexDirection: 'column',
          gap: '1.2rem',
        }}
      >
        {tabs.map((tab, index) => (
          <Flex
            data-testid={tab.title}
            variant="transparent"
            key={tab.title}
            sx={{
              gap: '0.4rem',
              p: '2.4rem',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: selectedTabIndex === index ? 'littleBoyBlue' : 'blueYonder20',
              background: selectedTabIndex === index ? 'littleBoyBlue5' : 'blue800',
              cursor: 'pointer',
            }}
            onMouseOver={() => {
              clearInterval(intervalId)
              setSelectedTabIndex(index)
            }}
            onMouseLeave={() => {
              clearInterval(intervalId)
              setTabInterval()
              setSelectedTabIndex(index)
            }}
          >
            <Flex sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <Text as="h2" sx={{ color: 'aliceBlue', fontWeight: 600 }}>
                {tab.title}
              </Text>
              <Text sx={{ color: 'lightSteelBlue', fontWeight: 500, fontSize: '1.4rem' }}>
                {tab.tabDescription}
              </Text>
            </Flex>
            {selectedTabIndex === index && (
              <Svg
                svg={ArrowRightIcon}
                sx={{ flexShrink: 0 }}
                descriptiveText="Right arrow indicating selected industry"
              />
            )}
          </Flex>
        ))}
      </Flex>
      <Flex
        sx={{
          maxWidth: ['62.8rem', '62.8rem', '100%'],
          width: ['100%', '100%', '49.5rem'],
          height: '100%',
          flexDirection: 'column',
          alignItems: ['center', 'center', 'flex-start'],
          justifyContent: ['center', 'center', 'flex-start'],
          textAlign: ['center', 'center', 'left'],
          border: '1px solid',
          borderColor: ['blueYonder50', 'blueYonder50', 'transparent'],
          px: ['1.6rem', '1.6rem', 0],
          pt: ['3.2rem', '3.2rem', 0],
          pb: ['1.6rem', '1.6rem', 0],
          borderRadius: '12px',
          position: 'relative',
        }}
        onMouseOver={() => {
          clearInterval(intervalId)
        }}
        onMouseLeave={() => {
          clearInterval(intervalId)
          setTabInterval()
        }}
      >
        <Flex
          sx={{
            height: '15rem',
            width: '100%',
            display: ['none', 'none', 'flex'],
          }}
        >
          <Svg
            sx={{
              position: 'absolute',
              left: '-5rem',
              top: '0rem',
            }}
            svg={tabs[selectedTabIndex].icon}
            descriptiveText={`Icon for ${tabs[selectedTabIndex].title} industry`}
          />
        </Flex>
        <Text
          as="h2"
          sx={{
            fontSize: ['2.1rem', '2.1rem', '3.2rem'],
            fontWeight: 600,
            mb: '0.8rem',
            color: 'aliceBlue',
          }}
        >
          {tabs[selectedTabIndex].contentTitle}
        </Text>
        <Text
          sx={{
            mb: '3.2rem',
            color: 'lightSteelBlue',
            fontSize: ['1.5rem', '1.5rem', '1.6rem'],
          }}
        >
          {tabs[selectedTabIndex].description}
        </Text>
        <Flex sx={{ flexDirection: 'column', mb: '3.2rem', gap: ['1.6rem', '1.6rem', '3.2rem'] }}>
          {tabs[selectedTabIndex].options.map(option => (
            <Flex key={option} sx={{ gap: '1.2rem', alignItems: 'center', textAlign: 'left' }}>
              <Svg
                svg={CheckIcon}
                sx={{ flexShrink: 0 }}
                descriptiveText="Checkmark for industry feature"
              />
              <Text sx={{ color: 'lightSteelBlue' }}>{option}</Text>
            </Flex>
          ))}
        </Flex>
        <Button
          data-testid={`learn-more-${selectedTabIndex}`}
          ariaLabel={`learn more ${selectedTabIndex}`}
          variant="roundedTertiary"
          sx={{ width: ['100%', 'auto'] }}
          onClick={() => {
            if (tabs[selectedTabIndex].link) {
              window.open(
                `${DOCS_CONTROL_D_DOMAIN}/docs${tabs[selectedTabIndex].link}`,
                '_blank',
                'noopener, noreferrer',
              )
            }

            if (tabs[selectedTabIndex]?.href) {
              navigate(tabs[selectedTabIndex].href || '')
            }
          }}
        >
          Learn more
        </Button>
      </Flex>
    </Flex>
  )
}

function IndustriesDropdown({
  selectedTabIndex,
  setSelectedTabIndex,
}: {
  selectedTabIndex: number
  setSelectedTabIndex: Setter<number>
}) {
  const hideRef = useRef<() => void>()
  const options: SimpleActionMenuItemProps[] = useMemo(
    () =>
      tabs?.map((tab, index) => ({
        isSelected: selectedTabIndex === index,
        isBackgroundHighlightedOnHover: true,
        ariaLabel: `${tab.title} option`,
        highlightedBackgroundColor: 'blueYonder20',
        buttonContent: <Text>{tab.title}</Text>,
        children: (
          <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text sx={{ color: index === selectedTabIndex ? 'cyan800' : 'aliceBlue' }}>
              {tab.contentTitle}
            </Text>
            {index === selectedTabIndex && (
              <Svg
                svg={DropdownCheckIcon}
                fill="cyan800"
                sx={{ flexShrink: 0 }}
                descriptiveText="Checkmark indicating selected industry in dropdown"
              />
            )}
          </Flex>
        ),
        onClick: () => {
          setSelectedTabIndex(index)
          hideRef.current?.()
        },
      })) ?? [],
    [selectedTabIndex, setSelectedTabIndex],
  )

  return (
    <SimpleDropdown
      ariaLabel={tabs[selectedTabIndex]?.contentTitle}
      testId="select-tab-dropdown"
      items={options}
      hideRef={hideRef}
      tooltipText={''}
      sx={{ display: ['flex', 'flex', 'none'], width: '100%', maxWidth: '62.8rem' }}
      defaultValue="Select Profile"
    />
  )
}
